import { Link, useParams } from "react-router-dom";
import { ArrowRight, ExternalLink, Check, X } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { SectionLabel } from "../components/site/SectionLabel";
import { getResource, RESOURCES } from "../data/resources";
import { trackEvent } from "../lib/api";
import NotFound from "./NotFound";

const Row = ({ label, children }) => (
  <div className="border-t border-brand-line py-6">
    <SectionLabel>{label}</SectionLabel>
    <div className="text-[16px] leading-relaxed text-brand-ink/85">{children}</div>
  </div>
);

export default function ResourceDetail() {
  const { slug } = useParams();
  const r = getResource(slug);
  if (!r) return <NotFound />;

  return (
    <div>
      <PageHero
        variant="light"
        label={`Resources · ${r.category}`}
        title={r.name}
        intro={r.tagline}
        crumbs={[{ label: "Resources", to: "/resources" }, { label: r.name }]}
      />

      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[2fr_1fr]">
          <div>
            <div className="mb-2 text-xs text-brand-muted">Last reviewed: {r.reviewed}</div>
            <Row label="What it does">{r.whatItDoes}</Row>
            <Row label="Best for">{r.bestFor}</Row>
            <Row label="Where it adds value">{r.addsValue}</Row>
            <Row label="Where it does not">{r.doesNot}</Row>
            <Row label="Who should consider it">{r.consider}</Row>
            <Row label="Who probably does not need it">{r.notFor}</Row>

            <div className="grid gap-6 border-t border-brand-line py-6 sm:grid-cols-2">
              <div>
                <SectionLabel>Pros</SectionLabel>
                <ul className="space-y-2">
                  {r.pros.map((p) => (
                    <li key={p} className="flex gap-2 text-sm text-brand-ink/85">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <SectionLabel>Limitations</SectionLabel>
                <ul className="space-y-2">
                  {r.limitations.map((p) => (
                    <li key={p} className="flex gap-2 text-sm text-brand-ink/85">
                      <X className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <Row label="Alternatives">{r.alternatives.join(", ")}</Row>
            <Row label="Pricing">{r.pricing}</Row>

            <div className="mt-4 border-l-2 border-brand-blue bg-brand-blue/5 px-6 py-5">
              <div className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-brand-blue">
                MATRIVON Assessment
              </div>
              <p className="text-[16px] leading-relaxed text-brand-ink/90">{r.assessment}</p>
            </div>
          </div>

          <aside className="space-y-8 lg:sticky lg:top-24 lg:self-start">
            <div className="border border-brand-line bg-white p-6">
              <a
                href={r.url}
                target="_blank"
                rel={r.affiliate ? "sponsored noopener noreferrer" : "noopener noreferrer"}
                onClick={() => trackEvent(r.affiliate ? "affiliate_click" : "resource_click", r.slug)}
                data-testid="resource-visit-link"
                className="inline-flex w-full items-center justify-center gap-2 bg-brand-blue px-5 py-3 text-sm font-semibold text-white hover:bg-brand-bluedark"
              >
                Visit {r.name} <ExternalLink className="h-4 w-4" />
              </a>
              {r.affiliate && (
                <p className="mt-3 text-xs text-brand-muted">
                  This link may be an affiliate link. See our Affiliate Disclosure.
                </p>
              )}
            </div>

            <div className="border border-brand-line bg-white p-6">
              <SectionLabel>More resources</SectionLabel>
              <ul className="space-y-2">
                {RESOURCES.filter((x) => x.slug !== slug).slice(0, 5).map((x) => (
                  <li key={x.slug}>
                    <Link to={`/resources/${x.slug}`} className="text-sm text-brand-muted hover:text-brand-blue">
                      {x.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <Link to="/resources" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:gap-2.5">
                All resources <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
